/**
 * Report Queue configuration + the single job-state vocabulary (spec 056).
 *
 * Everything the queue UI knows about timing and state labels lives here, so a
 * label or threshold is changed in ONE place and every surface (queue table,
 * drawer, notification center, toasts, handoff panel) moves together.
 */
import { QUEUE_PATH, SCHEDULES_PATH } from './reportRoutes'

/**
 * How long a foreground run may go before we hand it off to the queue.
 *
 * Product decision: 20 seconds, fixed for now but trivially changeable later.
 * Read an env override first so a future per-tenant/per-env change is a build
 * variable, not a code edit. NEVER write the literal 20000 anywhere else.
 */
const envHandoff = Number(import.meta.env?.VITE_QUEUE_HANDOFF_MS)
export const QUEUE_HANDOFF_MS = Number.isFinite(envHandoff) && envHandoff > 0 ? envHandoff : 20_000

/** Status poll interval for the queue list (matches the tray's cadence). */
export const POLL_MS = 5000

/**
 * How many runs the queue asks the SERVER for (CC-53049).
 *
 * The client used to send no `limit` at all, so it silently got the endpoint's default
 * of 25 — and then computed the tab counts and the pager client-side over just those
 * 25. On a tenant with 411 runs that meant "Didn't finish" could read 0 while ten
 * failures sat one page back.
 *
 * 50, decided by Jordan, to match the shared `DataGrid`'s own default so the queue
 * stops being the odd one out. The server caps `limit` at 200, so this is comfortably
 * inside it. Note this is the FETCH size, which is a different thing from the grid's
 * rows-per-page — `QueueTable` passes its own `pageSize` and a stored per-user choice
 * beats both.
 *
 * ⚠ Raising this has a real cost, so do not tune it casually: every active row in the
 * page gets a `status()` call on each 5-second poll, and each of those ADVANCES a job
 * server-side. Doubling the page doubles that fan-out. `dismissAll` also issues one
 * DELETE per job and there is still no bulk route.
 */
export const QUEUE_PAGE_SIZE = 50

/**
 * Mirror of the SCHEDULES list cap — `cb-api` `jobs/schedules.js` calls
 * `listSchedules(db, tenant, userId, 100, …)` with **no cursor**.
 *
 * So schedule 101 does not exist as far as the client is concerned, and unlike the
 * queue there is no paging to reach it. The schedules page discloses the cap in its
 * card header when the list is full, because a table implies completeness and a
 * silently truncated list of standing financial instructions is not something to
 * ship quietly.
 *
 * Display only — the server owns the real cap. If that number changes this MUST
 * follow, or the page will either stop warning when it should or warn when it
 * should not. Adding cursor paging to the route is what retires both.
 */
export const SCHEDULES_PAGE_CAP = 100

/**
 * Mirror of the runner's cap (cb-api `jobs/runner.js` MAX_RUN_MS). Display only —
 * the server owns the real cap. If the server value changes this MUST follow, or
 * the UI promises a ceiling the runner doesn't honour.
 *
 * ⚠ Known conflict (spec 056 §Open): reports that legitimately take 5–10 minutes
 * cannot complete under a 6-minute cap. Copy here states the honest limit rather
 * than promising longer. Raising it is a backend decision.
 */
export const MAX_RUN_MS = 6 * 60 * 1000

/**
 * Ad-hoc (non-scheduled) results are kept this long.
 *
 * ⚠ MIRROR of the server, display only. Three things must move together or the
 * UI lies about how long a result survives:
 *   1. `cb-api/src/domains/reports/jobs/routes.js` → `JOB_TTL_MS`
 *   2. the R2 lifecycle rule `expire-results` on the results bucket, which must
 *      be ≥ this value on BOTH qa and prod (prod bindings = CC-50151)
 *   3. this constant + `RESULT_RETENTION_LABEL` below
 *
 * Raised 24h → 1 week on 2026-08-07 (Jordan).
 */
export const RESULT_TTL_HOURS = 24 * 7

/**
 * How the retention window is written in UI copy. Derived, never hand-typed —
 * a literal "24 hours" in a component is how the copy and the real TTL drift.
 */
export const RESULT_RETENTION_LABEL =
  RESULT_TTL_HOURS % 24 === 0
    ? `${RESULT_TTL_HOURS / 24 === 7 ? '1 week' : `${RESULT_TTL_HOURS / 24} days`}`
    : `${RESULT_TTL_HOURS} hours`

/** Server-side job states, plus the two client-only states the UI adds. */
export const JOB_STATES = {
  queued: {
    key: 'queued',
    label: 'Queued',
    badge: 'bg-gray-100 text-gray-600',
    dot: 'bg-gray-400',
    icon: 'dots',
  },
  running: {
    key: 'running',
    label: 'Running',
    badge: 'bg-cb-accent/15 text-cb-accent-text',
    dot: 'bg-cb-accent',
    icon: 'spinner',
  },
  ready: {
    key: 'ready',
    label: 'Ready',
    badge: 'bg-cb-green/15 text-cb-green-dark',
    dot: 'bg-cb-green',
    icon: 'check',
  },
  // "Didn't finish" rather than "Failed": softer and more accurate for a timeout,
  // and failure is the state users care about most (design review, 2026-08-06).
  failed: {
    key: 'failed',
    label: "Didn't finish",
    badge: 'bg-red-100 text-red-700',
    dot: 'bg-red-500',
    icon: 'x',
  },
  /*
   * The user stopped it themselves (QA, 2026-09-03).
   *
   * `cancelled` has been a real SERVER state since spec 057 (`store.js` cancelJob
   * writes it, and the runner's two top-of-loop guards abandon on it), but it was
   * absent from this map, from TERMINAL_STATES and from every tab predicate. Spec
   * 111 then made Cancel reachable from the UI for the first time — so a cancelled
   * run landed in `jobState`'s fallback and rendered as a lower-case `cancelled`
   * chip that appeared under **All** and nowhere else. Measured live on QA D1: one
   * such row on vladdemo.
   *
   * Deliberately GREY, not red, and deliberately not the `x` icon's usual company:
   * the run did not fail, the user stopped it. It still belongs in "Didn't finish"
   * (see `matchesTab`) because that is where a person looks for a run that produced
   * no result — Jordan's own words for the defect.
   */
  cancelled: {
    key: 'cancelled',
    label: 'Cancelled',
    badge: 'bg-gray-100 text-gray-600',
    dot: 'bg-gray-400',
    icon: 'x',
  },
  /*
   * Expired — `text-gray-600` on the chip's own `bg-gray-100` (spec 152 §17, live QA
   * 2026-10-12). Every consumer paints this string whole onto one element, so the text
   * (and the bell's icon) always sits on the chip's own opaque ground, never the page's.
   *
   * Measured from what tailwindcss 4.2.1 emits (its palette is oklch; converted to the
   * sRGB the browser paints, WCAG 2.x relative luminance):
   *   text-gray-400 #99a1af on gray-100 #f3f4f6   2.364:1  FAIL  <- what shipped; read
   *                 live on the drawer chip at 13px/600 (spec 152 §16)
   *   text-gray-500 #6a7282 on gray-100 #f3f4f6   4.395:1  FAIL  <- not enough here
   *   text-gray-600 #4a5565 on gray-100 #f3f4f6   6.868:1  PASS  <- this
   *
   * The same pair Queued and Cancelled already use. Expired stays muted next to Ready
   * because it is achromatic grey against Ready's green. It is told apart from its grey
   * peers by its WORD, which is text and so is enough for SC 1.4.1 — not by its icon:
   * `clock` and Queued's `dots` both map to the same ClockIcon (stateIcons.js), so a
   * Queued chip and an Expired chip draw the same glyph in the same colours. Only
   * Cancelled's `x` differs. (A Queued row also carries a progress bar in the queue table,
   * and the bell draws a spinner for it; the drawer's header chip has only the word.)
   * A lighter-than-AA grey is not a way to say "faded": every aged-out scheduled run lands
   * here since spec 152, so this is now one of the commonest chips in the drawer.
   * Pinned in components/reports/queue/__tests__/retentionContrast.test.js.
   */
  expired: {
    key: 'expired',
    label: 'Expired',
    badge: 'bg-gray-100 text-gray-600',
    dot: 'bg-gray-300',
    icon: 'clock',
  },
  // Client-only: no server representation yet (scheduling is backend work).
  scheduled: {
    key: 'scheduled',
    label: 'Scheduled',
    badge: 'bg-cb-purple/15 text-cb-purple-dark',
    dot: 'bg-cb-purple',
    icon: 'clock',
  },
  recurring: {
    key: 'recurring',
    label: 'Recurring',
    badge: 'bg-cb-teal/15 text-cb-teal-dark',
    dot: 'bg-cb-teal',
    icon: 'refresh',
  },
}

/** States that mean "work is in flight". `prompt` is the tray's wait-or-notify pseudo-state. */
export const ACTIVE_STATES = new Set(['queued', 'running', 'prompt'])

/**
 * Terminal states — no further polling will change them.
 *
 * `cancelled` is terminal server-side too (`runner.js` TERMINAL_STATES, and
 * `cancelJob` refuses to re-cancel one), so leaving it out here made the client
 * keep polling a job the server had already abandoned.
 */
export const TERMINAL_STATES = new Set(['ready', 'failed', 'cancelled', 'expired'])

/**
 * Terminal states that produced NO RESULT to open — what "Didn't finish" means.
 *
 * Lives here with the rest of the state vocabulary rather than in the hook, for a
 * reason worth keeping: the Queue page's `matchesTab` and the bell's `inTab` are
 * two separate predicates over the same question, and they disagreed for weeks
 * (`cancelled` was in neither). One Set, imported by both — and `queueConfig` is
 * the module neither surface's tests mock, so it cannot go missing from a mock
 * the way a new hook export does.
 */
export const NO_RESULT_STATES = new Set(['failed', 'cancelled'])

/**
 * The EFFECTIVE state of a job, with expiry derived client-side.
 *
 * Nothing writes `state='expired'` server-side. The daily sweep DELETES expired
 * rows rather than marking them, so between expiry and the next sweep a stale row
 * still reads `ready` — it renders as clickable and then 410s on click.
 *
 * Verified live on QA 2026-08-06: 1 of 10 returned jobs was already past its
 * `expires_at` while still reporting `state: "ready"`. So this derivation is
 * fixing a real, currently-live defect, not guarding a hypothetical one.
 *
 * Only a READY job can expire — a failed job's details stay meaningful, and an
 * in-flight job past its TTL is a runner problem, not an expiry.
 *
 * Spec 152 — expiry is `resultExpiry`, not `expiresAt` alone. A schedule's run has
 * `expires_at = null`, so it never expired here, while the results bucket deletes its
 * object by AGE (about 7 days on QA): measured 2026-10-01, 51 of 69 ready scheduled runs
 * answered 410 under a Ready chip with Open and Download on offer.
 */
export function deriveState(job, now = Date.now()) {
  if (!job) return 'queued'
  const expiry = resultExpiry(job)
  if (job.state === 'ready' && expiry != null && expiry <= now) return 'expired'
  return job.state
}

/**
 * When this job's stored result stops being readable (epoch ms), or null when nothing
 * says. `resultExpiresAt` is the API's answer (spec 152: the EARLIER of the row's
 * `expires_at` and the results bucket's age); a payload without it — an older worker,
 * an environment that declares no bucket age — falls back to the row's own
 * `expiresAt`, which is exactly what this read before.
 */
export function resultExpiry(job) {
  if (!job) return null
  if (job.resultExpiresAt != null && Number.isFinite(Number(job.resultExpiresAt))) return Number(job.resultExpiresAt)
  if (job.expiresAt != null && Number.isFinite(Number(job.expiresAt))) return Number(job.expiresAt)
  return null
}

/** Is this job's stored result still fetchable? Drives whether a row is clickable. */
export function isResultAvailable(job, now = Date.now()) {
  return deriveState(job, now) === 'ready'
}

/** Look up the display vocabulary for a state, falling back to a safe default. */
export function jobState(state) {
  return JOB_STATES[state] || { key: state || 'unknown', label: state || 'Unknown', badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400', icon: 'dots' }
}

/**
 * Per-report failure guidance.
 *
 * Where we KNOW the real remedy we say it; where we don't we say the run stopped
 * and offer retry rather than inventing advice. Report 22 (Fee Receipts) is the
 * explicit case: CC-50022 proved that narrowing the date range makes it WORSE, so
 * we must never render the generic "try a shorter range" line for it.
 */
const FAILURE_ADVICE = {
  22: 'On this report a narrower date range doesn’t help — filtering by originator or client does.',
}

export function failureAdvice(reportId) {
  return FAILURE_ADVICE[Number(reportId)] || null
}

// Queue routes live with the other canonical Report Center routes; re-exported
// here so queue code has one import. Use these constants, never a literal path.
// Imported (not `export … from`) so the default-export object below can see them.
export { QUEUE_PATH, SCHEDULES_PATH }

export default {
  QUEUE_HANDOFF_MS,
  POLL_MS,
  QUEUE_PAGE_SIZE,
  SCHEDULES_PAGE_CAP,
  MAX_RUN_MS,
  RESULT_TTL_HOURS,
  RESULT_RETENTION_LABEL,
  JOB_STATES,
  ACTIVE_STATES,
  TERMINAL_STATES,
  jobState,
  deriveState,
  resultExpiry,
  isResultAvailable,
  failureAdvice,
  QUEUE_PATH,
  SCHEDULES_PATH,
}

/**
 * The progress line for a still-running job.
 *
 * CB writes its own text into `message`, and when the stored proc throws, that
 * text is a raw SQL exception — QA caught a row reading "Violation of PRIMARY KEY
 * constraint 'PK__#CheckLi__2BF336056C436333'. Cannot insert duplicate key in
 * object 'dbo.#CheckList'. The duplicate key value is (d2e3d7c4-…)." rendered as
 * the PROGRESS message, underneath a "Running" badge.
 *
 * Two things wrong with that, both ours to fix on this side:
 *
 *  1. It contradicts the badge. The row claims to be running while showing a
 *     terminal error, so the reader cannot tell whether to keep waiting. We do
 *     NOT infer a state from message text — the list projection carries no
 *     `error` field at all (only GET /reports/jobs/:id does), so there is no
 *     reliable signal here and guessing would be worse. The row keeps its real
 *     state and stops asserting a progress detail it cannot vouch for.
 *  2. It leaks internals — temp-table names, constraint names and row GUIDs are
 *     exactly what the secure-coding rule says never to surface.
 *
 * A message that looks like an exception dump is therefore replaced with the
 * neutral fallback. The FAILED state is unaffected: its wording is governed by
 * `failureAdvice` and the duration check, and a genuine failure still says so.
 */
const INTERNAL_ERROR_SIGNS = [
  /violation of .* constraint/i,
  /cannot insert duplicate key/i,
  /statement has been terminated/i,
  /\bdbo\.#?\w+/i,
  /\bPK__|\bIX__|\bFK__/,
  /system\.(data|exception)/i,
  /\bat [A-Z]\w+(\.\w+)+\(/,
  /sqlexception|stack trace/i,
]

export function progressMessage(job, fallback = 'Working…') {
  const raw = typeof job?.message === 'string' ? job.message.trim() : ''
  if (!raw) return fallback
  if (INTERNAL_ERROR_SIGNS.some((re) => re.test(raw))) return fallback
  return raw
}
